import { Appointment } from "../models/admissions.model";
import { Coverage } from "../models/coverages.model";
import {
  getOverallPriorAuthStatus,
  hasSavedPriorAuths,
} from "../models/prior-auth.model";
import { ApplicationCache } from "../models/application-cache.model";
import { EncounterStatus } from "../services/utils";

const AUTH_REQUIRED_LABEL = "Auth Required";
const NO_AUTH_REQUIRED_LABEL = EncounterStatus.NoAuthRequired;

/** Parse PHI `encounter_status.authRequirementStatus` → yes/no decision for this DOS. */
export function authRequirementStatusToRequiresAuth(
  authRequirementStatus: string | null | undefined
): boolean | undefined {
  const normalized = String(authRequirementStatus ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) {
    return undefined;
  }
  if (normalized === "auth required") {
    return true;
  }
  if (normalized === "no auth required") {
    return false;
  }
  return undefined;
}

export function requiresAuthToAuthRequirementStatus(
  requiresAuth: boolean | undefined | null
): string | null {
  if (requiresAuth === undefined || requiresAuth === null) {
    return null;
  }
  return requiresAuth ? AUTH_REQUIRED_LABEL : NO_AUTH_REQUIRED_LABEL;
}

export function getEncounterRequiresAuth(
  appointment: Appointment | null | undefined
): boolean | undefined {
  return authRequirementStatusToRequiresAuth(
    appointment?.authRequirementStatus ?? null
  );
}

/**
 * Schedule / referrals grid: resolve requires-auth when list-clinic omits
 * `authRequirementStatus` but still sends workflow `authStatus` or coverage hints.
 */
export function resolveEncounterRequiresAuthForListView(
  appointment: Appointment | null | undefined,
  primaryCoverage?: Coverage | null
): boolean | undefined {
  const fromRequirement = getEncounterRequiresAuth(appointment);
  if (fromRequirement !== undefined) {
    return fromRequirement;
  }

  const authStatus = String(appointment?.authStatus ?? "")
    .trim()
    .toLowerCase();
  if (authStatus === NO_AUTH_REQUIRED_LABEL.toLowerCase()) {
    return false;
  }
  if (authStatus === AUTH_REQUIRED_LABEL.toLowerCase()) {
    return true;
  }

  if (primaryCoverage?.requiresAuth === true) {
    return true;
  }
  if (primaryCoverage?.requiresAuth === false) {
    return false;
  }

  return undefined;
}

export function getEncounterAuthRequirementLabel(
  appointment: Appointment | null | undefined
): string | null {
  const trimmed = String(appointment?.authRequirementStatus ?? "").trim();
  return trimmed || null;
}

/** Matches PHI `deriveAuthRequirementStatus` / worklist display rules. */
export function deriveAuthRequirementStatusLabel(
  requiresAuth: boolean | undefined | null,
  priorAuthStatus: string | null | undefined
): string | null {
  if (requiresAuth === undefined || requiresAuth === null) {
    return null;
  }
  if (requiresAuth === false) {
    return NO_AUTH_REQUIRED_LABEL;
  }
  if (String(priorAuthStatus ?? "").trim() === "Authorized") {
    return null;
  }
  return AUTH_REQUIRED_LABEL;
}

/** Matches PHI `deriveAuthWorkflowStatus` for `encounter_status.authStatus`. */
export function deriveAuthWorkflowStatusLabel(
  requiresAuth: boolean | undefined | null,
  priorAuthStatus: string | null | undefined
): string | null {
  if (requiresAuth === undefined || requiresAuth === null) {
    return null;
  }
  if (requiresAuth === false) {
    return NO_AUTH_REQUIRED_LABEL;
  }
  const prior = String(priorAuthStatus ?? "").trim();
  if (prior === "Authorized") {
    return EncounterStatus.Authorized;
  }
  if (prior === "Denied") {
    return EncounterStatus.Denied;
  }
  return EncounterStatus.PendingAuth;
}

export function deriveExpectedAuthStatusLabelFromCache(
  cache: ApplicationCache
): string | null {
  const requiresAuth = getEncounterRequiresAuth(cache.appointment);
  if (requiresAuth === undefined) {
    return null;
  }
  if (!requiresAuth) {
    return EncounterStatus.NoAuthRequired;
  }
  const hasPriorAuth = hasSavedPriorAuths(cache.priorAuths, cache.priorAuth);
  const overallPriorAuthStatus = getOverallPriorAuthStatus(
    cache.priorAuths,
    cache.priorAuth
  );
  if (!hasPriorAuth) {
    return EncounterStatus.PendingAuth;
  }
  if (overallPriorAuthStatus === "Authorized") {
    return EncounterStatus.Authorized;
  }
  if (overallPriorAuthStatus === "Denied") {
    return EncounterStatus.Denied;
  }
  return EncounterStatus.PendingAuth;
}

export function deriveExpectedAuthRequirementStatusLabelFromCache(
  cache: ApplicationCache
): string | null {
  const requiresAuth = getEncounterRequiresAuth(cache.appointment);
  if (requiresAuth === undefined) {
    return null;
  }
  const prior = getOverallPriorAuthStatus(cache.priorAuths, cache.priorAuth);
  return deriveAuthRequirementStatusLabel(requiresAuth, prior);
}

export function isAuthRequiredForEncounterCpts(
  cache: ApplicationCache
): boolean {
  return getEncounterRequiresAuth(cache.appointment) === true;
}
